import {css} from '@mui/material';
import {DlMuiDynamicIcon} from '@wallaby-ui/core';
import React from 'react';
import {useIntl} from 'react-intl';

import {AlertTitleTooltip} from './AlertTitleTooltip';

type Props = {
  weight: number;
  /**
   * 'panel' is the light 11px pill under the category icon of an Alerts panel row (`.wt` of the design), 'tooltip' the
   * 11px pill of the row bell's tooltip, 'board' the 12px pill of the alert board and the alerts list tables.
   */
  variant?: 'panel' | 'tooltip' | 'board';
  /** Greyed out, as the alerts list shows the weight of an episode that is not Active and so not in the total. */
  muted?: boolean;
  style?: React.CSSProperties;
};

const badgeCss = css`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 1.3;
  font-family: var(--dlFontFamilySemiBold);
  color: var(--dlThemeGray-700);
  background: var(--dlThemeGray-100);
  border-radius: 6px;
  padding: 2px 7px;
  white-space: nowrap;
  &[data-variant='panel'] {
    color: var(--dlThemeGray-600);
    background: var(--dlThemeGray-50);
    padding: 2px 6px;
  }
  &[data-variant='board'] {
    gap: 4px;
    font-size: 12px;
    padding: 2px 8px;
  }
  &[data-muted='true'] {
    color: var(--dlThemeGray-400);
    background: var(--dlThemeGray-50);
  }
`;
const iconCss = css`
  font-size: 11px;
`;
const panelIconCss = css`
  font-size: 12px;
  opacity: 0.8;
`;
const boardIconCss = css`
  font-size: 12px;
`;
const ICON_CSS = {panel: panelIconCss, tooltip: iconCss, board: boardIconCss};

/** The alert weight as a pill with the balanced-scale glyph, shared by every alert surface. */
export const AlertWeightBadge: React.FC<Props> = (props) => {
  const {weight, variant = 'panel', muted = false, style} = props;
  const intl = useIntl();
  // the row bell's tooltip is itself a tooltip: no second one over its badge
  return (
    <AlertTitleTooltip title={variant === 'tooltip' ? undefined : intl.formatMessage({id: 'alert.weight'})}>
      <span css={badgeCss} style={style} data-name={'dl-alert-weight-badge'} data-variant={variant} data-muted={muted}>
        <DlMuiDynamicIcon
          iconName='scaleBalancedIcon'
          useFixedViewBox='0 0 640 512'
          noButtonStyles
          style={{color: 'inherit', padding: 0, margin: 0, pointerEvents: 'none'}}
          svgIconInputCss={ICON_CSS[variant]}
        />
        {weight}
      </span>
    </AlertTitleTooltip>
  );
};

AlertWeightBadge.displayName = 'AlertWeightBadge';
